import { useTranslation } from 'react-i18next'
import { Button } from '@claritio/frontend/components/ui/button'
import { apiUrl } from '@claritio/frontend/lib/api'

// Build-time mirror of the backend STAFF_EMAIL_DOMAIN; a hint only.
const STAFF_DOMAIN = (import.meta.env.VITE_STAFF_EMAIL_DOMAIN as string | undefined)?.trim().toLowerCase() || 'claritio.com'

export function isStaffEmail(email?: string): boolean {
  return (email ?? '').trim().toLowerCase().endsWith(`@${STAFF_DOMAIN}`)
}

interface OAuthButtonsProps {
  returnTo: string
  inviteToken?: string
  /** ENG-216 — 'after' puts the divider below the buttons, for call sites that
   *  render OAuth above the credentials form. Defaults to today's ordering. */
  divider?: 'before' | 'after'
  /** Typed email; a staff-domain address adds the Google `hd` hint. Only a hint,
   *  the backend verifies the claim itself. */
  email?: string
}

// ENG-204 — OAuth sign-in/signup buttons. Renders "Continue with Google" and
// "Continue with Microsoft" either side of a divider (see `divider`). Each
// button navigates the browser (full-page, not fetch) to the backend's
// /api/v1/oauth/start endpoint, which stores return_to + invite_token in the
// session and redirects to the provider.
// The URL must be absolute (resolved via apiBaseFor, same as the API client):
// a relative "/api/v1/..." would be resolved against the SPA host and swallowed
// by nginx's try_files → index.html fallback, never reaching the controller.
export default function OAuthButtons({ returnTo, inviteToken, divider = 'before', email }: OAuthButtonsProps) {
  const { t } = useTranslation('admin')

  function oauthStartUrl(provider: string): string {
    const params = new URLSearchParams({ provider, return_to: returnTo, origin: window.location.origin })
    if (inviteToken) params.set('invite_token', inviteToken)
    if (provider === 'google_oauth2' && isStaffEmail(email)) params.set('hd', STAFF_DOMAIN)
    return apiUrl(`/api/v1/oauth/start?${params.toString()}`)
  }

  const rule = (
    <div className="relative my-4">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t" />
      </div>
      <div className="relative flex justify-center text-label uppercase">
        <span className="bg-card px-2 text-muted-foreground">{t('oauth.or', 'or')}</span>
      </div>
    </div>
  )

  return (
    <div className="space-y-2">
      {divider === 'before' && rule}
      <Button
        type="button"
        variant="outline"
        className="w-full border-border-strong bg-card text-card-foreground hover:bg-secondary hover:text-secondary-foreground"
        onClick={() => { window.location.href = oauthStartUrl('google_oauth2') }}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {t('oauth.google', 'Continue with Google')}
      </Button>
      <Button
        type="button"
        variant="outline"
        className="w-full border-border-strong bg-card text-card-foreground hover:bg-secondary hover:text-secondary-foreground"
        onClick={() => { window.location.href = oauthStartUrl('microsoft_oidc') }}
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#7FBA00" d="M13 1h10v10H13z" />
          <path fill="#00A4EF" d="M1 13h10v10H1z" />
          <path fill="#FFB900" d="M13 13h10v10H13z" />
        </svg>
        {t('oauth.microsoft', 'Continue with Microsoft')}
      </Button>
      {divider === 'after' && rule}
    </div>
  )
}
